import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import saveAs from "file-saver";
import Bugsnag from "@bugsnag/js";
import { getQueryParams } from "@/utils/httpRequest";
import { baseUrl, onPrepareHeaders } from "@/redux/services/helpers";
import { showNotification } from "@/redux/actions/notification";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS, SORT_OPTIONS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics } from "@/utils/analytics";
import { borrowerNeedsListService } from "@/redux/services/borrowerNeedsListService";
import { setProcessingState } from "@/redux/reducers/modalConfirmation";
import { loanSummaryService } from "@/features/LoanSummary/Management/store/services/loanSummaryService";

export const defaultBorrowerNeedsListFileParameters = {
  page: 1,
  size: 50,
  sortBy: SORT_OPTIONS[1].id,
  searchTerm: "",
};

export const borrowerNeedsListFileService = createApi({
  reducerPath: "borrowerNeedsListFileApi",
  tagTypes: ["BorrowerNeedsListFile"],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getBorrowerNeedsListFiles: builder.query({
      query: ({ loanId, borrowerId, needListId, ...params }) =>
        `loans/${loanId}/borrowers/${borrowerId}/needs-list/${needListId}/files?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              ...result.items.map(({ needListFileId }) => ({ type: "BorrowerNeedsListFile", needListFileId })),
              { type: "BorrowerNeedsListFile", id: "LIST" },
            ]
          : [{ type: "BorrowerNeedsListFile", id: "LIST" }],
    }),
    deleteBorrowerNeedsListFile: builder.mutation({
      query({ loanId, borrowerId, needListId, needListFileId }) {
        return {
          url: `loans/${loanId}/borrowers/${borrowerId}/needs-list/${needListId}/files/${needListFileId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [{ type: "BorrowerNeedsListFile", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.NEEDS_LIST_DOCUMENT_FILE_DELETED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File removed from the borrower needs list!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(borrowerNeedsListService.util.invalidateTags([{ type: "BorrowerNeedsList", id: "LIST" }]));
          dispatch(loanSummaryService.util.invalidateTags([{ type: "LoanSummaryNeedListFile", id: "LIST" }]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    approveBorrowerNeedsListFile: builder.mutation({
      query({ loanId, borrowerId, needListId, needListFileId }) {
        return {
          url: `loans/${loanId}/borrowers/${borrowerId}/needs-list/${needListId}/files/${needListFileId}/approval`,
          method: "POST",
        };
      },
      invalidatesTags: [{ type: "BorrowerNeedsListFile", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.NEEDS_LIST_DOCUMENT_FILE_APPROVED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File has been approved!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(borrowerNeedsListService.util.invalidateTags([{ type: "BorrowerNeedsList", id: "LIST" }]));
          dispatch(loanSummaryService.util.invalidateTags([{ type: "LoanSummaryNeedListFile", id: "LIST" }]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    denyBorrowerNeedsListFile: builder.mutation({
      query({ loanId, borrowerId, needListId, needListFileId, reason }) {
        return {
          url: `loans/${loanId}/borrowers/${borrowerId}/needs-list/${needListId}/files/${needListFileId}/denial`,
          method: "POST",
          body: { reason },
        };
      },
      invalidatesTags: [{ type: "BorrowerNeedsListFile", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.NEEDS_LIST_DOCUMENT_FILE_REJECTED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File has been rejected!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(borrowerNeedsListService.util.invalidateTags([{ type: "BorrowerNeedsList", id: "LIST" }]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    previewBorrowerNeedsListFile: builder.mutation({
      query({ loanId, borrowerId, needListId, needListFileId }) {
        return {
          url: `loans/${loanId}/borrowers/${borrowerId}/needs-list/${needListId}/files/${needListFileId}/preview`,
          method: "GET",
        };
      },
      invalidatesTags: [{ type: "BorrowerNeedsListFile", id: "LIST" }],
      async onQueryStarted(id, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = data.url;
          a.target = "_blank";
          a.download = data.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          sendAnalytics(ANALYTIC_EVENTS.NEEDS_LIST_DOCUMENT_FILE_PREVIEW, data);
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    downloadBorrowerNeedsListFile: builder.mutation({
      query({ loanId, borrowerId, needListId, needListFileId }) {
        return {
          url: `loans/${loanId}/borrowers/${borrowerId}/needs-list/${needListId}/files/${needListFileId}/physical`,
          method: "GET",
        };
      },
      invalidatesTags: [{ type: "BorrowerNeedsListFile", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = data.url;
          a.download = data.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          sendAnalytics(ANALYTIC_EVENTS.NEEDS_LIST_DOCUMENT_FILE_DOWNLOAD, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File has been downloaded!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    downloadLoanPackage: builder.mutation({
      queryFn: async (id, api, extraOptions, baseQuery) => {
        try {
          api.dispatch(setProcessingState(true));
          const result = await baseQuery({
            url: `loans/${id}/needs-list/package`,
            responseHandler: response => response.blob(),
          });

          saveAs(result.data, "Loan Package.zip");

          api.dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Package generated successfully! The file has been automatically saved to your device.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );

          api.dispatch(setProcessingState(false));
          return { data: result.data };
        } catch (err) {
          api.dispatch(setProcessingState(false));
          Bugsnag.notify(err);
          return { error: err };
        }
      },
    }),
  }),
});

export const {
  useGetBorrowerNeedsListFilesQuery,
  useDeleteBorrowerNeedsListFileMutation,
  useApproveBorrowerNeedsListFileMutation,
  useDenyBorrowerNeedsListFileMutation,
  usePreviewBorrowerNeedsListFileMutation,
  useDownloadBorrowerNeedsListFileMutation,
  useDownloadLoanPackageMutation,
} = borrowerNeedsListFileService;
